import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Package, Printer, X, Plus, Trash2, RefreshCw, ChevronDown, ChevronUp, Edit2, Camera, Upload } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanDelete } from '../../components/PermissionGuard';
import { packingLabelInnerHtml, packingLabelPrintDoc, PACKING_LABEL_CSS, getBoxSubLabels } from '../../utils/packingLabel';
import BoxModal from '../../components/PackingBoxModal';
import PackingChallanReport from '../admin/PackingChallanReport';
import DeliveryBoxesReport from '../admin/DeliveryBoxesReport';

const safeDate = (d) => { try { return d && isValid(parseISO(d)) ? format(parseISO(d), 'dd MMM yyyy') : '-'; } catch { return '-'; } };

// ── Label Print (shared format: matches physical label + item image) ──────────
function BoxLabel({ box, onClose }) {
  const [copies, setCopies] = React.useState(Math.max(1, Math.min(26, parseInt(box.copies) || 1)));

  const labels = React.useMemo(() => {
    const SUB = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const base = box.box_number || '';
    const n = Math.max(1, Math.min(26, copies));
    if (n === 1) return [base];
    return Array.from({ length: n }, (_, i) => base + '-' + SUB[i]);
  }, [box.box_number, copies]);

  const handleCopiesChange = async (next) => {
    const v = Math.max(1, Math.min(26, next));
    setCopies(v);
    // Persist to backend so admin page and Export PDF also reflect new count
    try { await api.put('/packing/boxes/' + box.id + '/copies', { copies: v }); }
    catch { /* non-critical — label still prints correctly */ }
  };

  const handlePrint = () => {
    const win = window.open('', '_blank');
    win.document.write(packingLabelPrintDoc(labels.map(dn => packingLabelInnerHtml(box, dn)).join('\n')));
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 500);
  };

  // Preview uses the EXACT same HTML that prints (scaled to fit on screen).
  const innerHtml = `<style>${PACKING_LABEL_CSS}</style>${packingLabelInnerHtml(box, labels[0])}`;

  return (
    <div className="modal-backdrop">
      <div className="modal-scroll">
        <div className="modal-box" style={{maxWidth:480,display:"flex",flexDirection:"column",maxHeight:"90vh"}}>
        <div className="modal-header flex-shrink-0">
          <h3 className="font-bold flex items-center gap-2"><Printer className="w-5 h-5 text-blue-600" /> Label Preview — {box.box_number}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          {/* Copies stepper */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-xs font-semibold text-amber-800 mb-2">Kitne copies? (Sub-labels -A, -B, -C... banenge)</p>
            <div className="flex items-center gap-3">
              <button onClick={() => handleCopiesChange(copies - 1)}
                className="w-9 h-9 bg-white border border-amber-300 rounded-lg font-bold text-amber-700 hover:bg-amber-100 flex items-center justify-center text-lg">−</button>
              <input type="number" min={1} max={26} value={copies}
                onChange={e => handleCopiesChange(parseInt(e.target.value) || 1)}
                className="w-16 text-center border border-amber-300 rounded-lg py-1.5 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-amber-500" />
              <button onClick={() => handleCopiesChange(copies + 1)}
                className="w-9 h-9 bg-white border border-amber-300 rounded-lg font-bold text-amber-700 hover:bg-amber-100 flex items-center justify-center text-lg">+</button>
              <span className="flex-1 text-xs text-gray-500">
                {copies === 1
                  ? <>Single label: <strong>{box.box_number}</strong></>
                  : <>Labels: <strong className="text-amber-700">{labels.slice(0,3).join('  ')}{copies > 3 ? '  …  ' + labels[copies-1] : ''}</strong></>}
              </span>
            </div>
          </div>

          {/* Preview */}
          <p className="text-xs text-gray-400 text-center">Preview (pehla label)</p>
          <div className="flex justify-center">
            <div style={{ transform: 'scale(0.95)', transformOrigin: 'top center' }}
                 dangerouslySetInnerHTML={{ __html: innerHtml }} />
          </div>
        </div>
        <div className="p-4 border-t flex gap-3 flex-shrink-0">
          <button onClick={onClose} className="btn-secondary" style={{flex:1}}>Close</button>
          <button onClick={handlePrint} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2">
            <Printer className="w-4 h-4" /> Print {copies > 1 ? `${copies} Labels` : 'Label'}
          </button>
        </div>
      </div>
        </div>
    </div>
  );
}

// ── Box Photos ────────────────────────────────────────────────────────────────
function BoxPhotos({ boxId }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    api.get(`/packing/boxes/${boxId}/photos`).then(r => setPhotos(r.data)).catch(() => {});
  }, [boxId]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const form = new FormData();
    files.forEach(f => form.append('photos', f));
    try {
      const r = await api.post(`/packing/boxes/${boxId}/photos`, form);
      toast.success(`${files.length} photo(s) uploaded! ✅`);
      if (r.data.photos?.length > 0) setPhotos(r.data.photos);
      else api.get(`/packing/boxes/${boxId}/photos`).then(r2 => setPhotos(r2.data));
      setShowGrid(true);
    } catch (err) { toast.error(err?.response?.data?.message || 'Upload fail'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleDelete = async (photoId) => {
    try {
      await api.delete(`/packing/boxes/${boxId}/photos/${photoId}`);
      setPhotos(p => p.filter(ph => ph.id !== photoId));
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <div className="flex items-center justify-between mb-2">
        <button onClick={() => setShowGrid(!showGrid)} className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700">
          <Camera className="w-3.5 h-3.5" /> Photos {photos.length > 0 && `(${photos.length})`}
          {photos.length > 0 && (showGrid ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
        </button>
        <button onClick={() => fileRef.current.click()} disabled={uploading}
          className="flex items-center gap-1 text-xs bg-blue-500 hover:bg-blue-600 text-white px-2.5 py-1 rounded-lg font-medium disabled:opacity-50">
          <Upload className="w-3 h-3" /> {uploading ? 'Upload...' : 'Upload'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
      </div>
      {showGrid && (
        photos.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-3 bg-gray-50 rounded-xl">No photos yet — Upload some</p>
        ) : (
          <div className="grid grid-cols-4 gap-2">
            {photos.map(photo => (
              <div key={photo.id} className="relative group">
                <img src={photo.image_url} alt="box" onClick={() => setLightbox(photo)}
                  className="w-full h-20 object-cover rounded-xl border border-gray-200 cursor-pointer group-hover:ring-2 group-hover:ring-blue-400 transition-all"
                  onError={e => { e.target.style.display = 'none'; }} />
                <button onClick={() => handleDelete(photo.id)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )
      )}
      {lightbox && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <div className="relative max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <img src={lightbox.image_url} alt="full" className="w-full rounded-2xl shadow-2xl" />
            <button onClick={() => setLightbox(null)} className="absolute top-2 right-2 bg-white/20 text-white rounded-full p-1.5"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Box Card ──────────────────────────────────────────────────────────────────
function BoxCard({ box, onPrint, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const totalItems = box.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-lg font-bold text-gray-900 tracking-wide">{box.box_number}</span>
              {box.main_item && <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full font-medium">{box.main_item}</span>}
            </div>
            {box.photo_code && <span className="text-xs font-mono bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded mt-0.5 inline-block">📷 {box.photo_code}</span>}
            {box.project_name && <p className="text-xs text-gray-500 mt-0.5">📁 {box.project_name}</p>}
            <p className="text-xs text-gray-400 mt-1">{box.items?.length || 0} items • {totalItems} units • {safeDate(box.created_at)}</p>
          </div>
          <div className="flex gap-1.5 flex-shrink-0">
            <button onClick={() => onPrint(box)} className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl"><Printer className="w-4 h-4" /></button>
            <button onClick={() => onEdit(box)} className="p-2 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-xl"><Edit2 className="w-4 h-4" /></button>
            <button onClick={() => onDelete(box.id)} className="p-2 border border-red-100 text-red-400 hover:bg-red-50 rounded-xl"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>

        {box.items?.length > 0 && (
          <button onClick={() => setExpanded(!expanded)} className="mt-2 text-xs text-gray-400 flex items-center gap-1 hover:text-gray-600">
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? 'Hide Contents' : `Contents (${box.items.length})`}
          </button>
        )}
        {expanded && (
          <div className="mt-2 space-y-1">
            {box.items.map((item, i) => (
              <div key={i} className="flex justify-between text-xs bg-gray-50 rounded-lg px-3 py-1.5">
                <span className="text-gray-700">{i+1}. {item.item_name}</span>
                <span className="font-bold text-gray-600">{item.quantity} {item.unit}</span>
              </div>
            ))}
          </div>
        )}
        <BoxPhotos boxId={box.id} />
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
// A box printed with label copies (A, B…) is one physical box per label
const physicalBoxes = list => list.reduce((s, b) => s + Math.max(1, Math.min(26, parseInt(b.copies) || 1)), 0);

export default function WorkerPackingPage() {
  const { hasAccess, canWrite, canEdit, canDelete } = usePermission('packing');
  const [boxes, setBoxes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextBoxNum, setNextBoxNum] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editBox, setEditBox] = useState(null);
  const [printBox, setPrintBox] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // Same reports as the admin Packing page, for anyone with the Packing permission
  const [tab, setTab] = useState('boxes'); // 'boxes' | 'challan' | 'delivery'

  const load = async (soft = false) => {
    if (soft) setRefreshing(true); else setLoading(true);
    try {
      const [boxesRes, projectsRes, nextNumRes] = await Promise.all([
        api.get('/packing/boxes'),
        api.get('/projects').catch(() => ({ data: [] })),
        api.get('/packing/next-box-number').catch(() => ({ data: { box_number: 'BOX-01' } })),
      ]);
      setBoxes(boxesRes.data);
      setProjects(projectsRes.data);
      setNextBoxNum(nextNumRes.data.box_number);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this box?')) return;
    try {
      await api.delete(`/packing/boxes/${id}`);
      toast.success('Box deleted');
      load(true);
    } catch { toast.error('Delete failed'); }
  };

  return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className={`${tab === 'boxes' ? 'max-w-2xl' : 'max-w-7xl'} mx-auto px-3 py-4 space-y-4 pb-10`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Package className="w-6 h-6 text-purple-600" /> Packing</h1>
          <p className="text-xs text-gray-400 mt-0.5">{tab === 'boxes'
            ? `${physicalBoxes(boxes)} boxes${physicalBoxes(boxes) !== boxes.length ? ` (${boxes.length} entries)` : ''}`
            : tab === 'delivery' ? 'Boxes delivered on challans vs pending' : 'Packed boxes on Sale Challans vs pending'}</p>
        </div>
        {tab === 'boxes' && <div className="flex gap-2">
          <button onClick={() => load(true)} disabled={refreshing} className="p-2 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-xl">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <CanWrite canWrite={canWrite}>
            <button onClick={() => { setEditBox(null); setShowCreateModal(true); }}
              className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-xl text-sm font-bold">
              <Plus className="w-4 h-4" /> New Box
            </button>
          </CanWrite>
        </div>}
      </div>

      <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-xl w-fit max-w-full">
        {[['boxes', 'Boxes'], ['challan', 'Sale Challan vs Packing'], ['delivery', 'Delivery Challan vs Boxes']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${tab === k ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'challan' ? <PackingChallanReport projects={projects} />
        : tab === 'delivery' ? <DeliveryBoxesReport projects={projects} />
        : loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full" /></div>
      ) : boxes.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <Package className="w-12 h-12 mx-auto mb-3 text-gray-200" />
          <p className="font-bold text-gray-400">No boxes yet</p>
          <p className="text-xs text-gray-300 mt-1">Create your first box with the New Box button</p>
        </div>
      ) : (
        <div className="space-y-3">
          {boxes.map(box => (
            <BoxCard key={box.id} box={box}
              onPrint={setPrintBox}
              onEdit={(b) => { setEditBox(b); setShowCreateModal(true); }}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {showCreateModal && (
        <BoxModal projects={projects} editBox={editBox} nextBoxNum={nextBoxNum}
          onClose={() => { setShowCreateModal(false); setEditBox(null); }}
          onSaved={async (freshBox) => {
            setShowCreateModal(false);
            setEditBox(null);
            if (freshBox) {
              // Edit: update the specific box in state instantly using PUT response
              setBoxes(prev => prev.map(b => b.id === freshBox.id ? freshBox : b));
              // Update label preview too if it's open for this box
              if (printBox && printBox.id === freshBox.id) setPrintBox(freshBox);
            } else {
              // Create: reload all boxes to get the new one
              const res = await api.get('/packing/boxes').catch(() => null);
              if (res) setBoxes(res.data);
            }
          }}
        />
      )}

      {printBox && <BoxLabel box={printBox} onClose={() => setPrintBox(null)} />}
    </div>
    </PermissionGuard>
  );
}

